import {QueryOptions} from '@tanstack/react-query';
import {get} from 'lodash';
import {Paginated, tenantApi} from '../index';
import {mapQueryKeyToSearchParams} from '../utils';
import {CycleToWorkSettings} from '../../views/account-settings/requestObjects/intex';
import {
  AdminUpdateTenantRequest,
  CreateTenantRequest,
  FullDetailsDto,
  GlobalDetailsRequest,
  ITenantSettingsSalSacForm,
  NetPayLoanDto,
  TenantDto,
  TenantInvitationInfo,
  TenantSettingsDto,
  TenantSettingsExtendedDto,
  UpdateTenantAgreementAsAdminRequest,
  UpdateTenantRequest,
} from './dto';

export async function createEmployer(data: CreateTenantRequest) {
  return await tenantApi.post<{ tenant: TenantDto }>('', data);
}

export async function getPayLoanSettings(accountNumber: string) {
  return tenantApi.get<NetPayLoanDto>('/net-pay-loan', { accountNumber });
}

export async function getTenantSettings() {
  return await tenantApi.get<TenantSettingsExtendedDto>('/settings');
}

export async function getSettings({ queryKey }: QueryOptions) {
  const accountNumber = get(queryKey, '0.1');
  if (!accountNumber) {
    throw new Error('Missing account number');
  }

  return await tenantApi.get<ITenantSettingsSalSacForm>(
    '/sal-sac-form-settings',
    { accountNumber },
  );
}

export async function updateGlobalTenantSettings(data: GlobalDetailsRequest) {
  return await tenantApi.put<TenantSettingsDto>('/global-settings', data);
}

export async function deactivateAccountReq(id: number) {
  return await tenantApi.get<TenantSettingsDto>(`/deactivate/${id}`);
}

export async function deleteAccountReq(id: number) {
  return await tenantApi.delete<TenantSettingsDto>(`/delete/${id}`);
}

export async function activateAccountReq(id: number) {
  return await tenantApi.get<void>(`/activate/${id}`);
}

export interface UpdateCycleToWorkSettingsRequest
  extends Omit<CycleToWorkSettings, 'currentYearSchemeLimit' | 'certificateApplicationDatePeriod'> {
  certificateApplicationDatePeriod: {
    startDate: string | undefined;
    endDate: string | undefined;
    active: boolean;
  };
}

export interface UpdateWelcomeSettingsRequest {
  companyDescription: string;
}

export async function updateCycleToWorkTenantSettings(data: UpdateCycleToWorkSettingsRequest) {
  const response = await tenantApi.put<TenantSettingsDto>('/cycle-to-work-settings', data);

  return response;
}

export async function updateWelcomeSettings(data: UpdateWelcomeSettingsRequest) {
  const response = await tenantApi.put<UpdateWelcomeSettingsRequest>('/welcome-settings', data);

  return response;
}

export async function getAllTenants({ queryKey }: QueryOptions) {
  return await tenantApi.get<Paginated<TenantDto>>(
    '',
    mapQueryKeyToSearchParams(queryKey),
  );
}

export async function getTenantSettingsById({ queryKey }: QueryOptions) {
  const tenantId = get(queryKey, '0.1');
  if (!tenantId) {
    throw new Error('Missing tenant id');
  }

  return await tenantApi.get<FullDetailsDto>(`/${tenantId}/settings`);
}

export async function getInvitationTenantSettingsById({ queryKey }: QueryOptions) {
  const slug = get(queryKey, '0.1');
  if (!slug) {
    throw new Error('Missing tenant id');
  }

  return await tenantApi.get<TenantInvitationInfo>('/invitation-settings', { accountNumber: slug });
}

export async function confirmTenantRegistration(tenantId: number) {
  return await tenantApi.post<{ userIds: number[]; tenantAccountNumber: string }>(
    '/confirm-registration',
    { tenantId },
  );
}

export async function adminUpdateTenantById(tenantId: string, body: AdminUpdateTenantRequest) {
  return await tenantApi.put(`/admin/${tenantId}`, body);
}

export async function updateTenant(body: UpdateTenantRequest): Promise<TenantSettingsDto> {
  return await tenantApi.put('', body);
}

export async function updateTenantAgreementAsAdmin(
  id: number,
  request: UpdateTenantAgreementAsAdminRequest,
) {
  return await tenantApi.post<TenantDto>(`/update-agreement/${id}`, request);
}
